import { Stack } from '@fluentui/react';
import { mergeClasses, SelectTabData, Tab, TabList } from '@fluentui/react-components';
import { DataUsageRegular, FolderRegular, TableRegular } from '@fluentui/react-icons';
import { NotificationType, useFabricContext } from '@idmc-ms-fabric/integration';
import { IBrowseConnection, ICdgcObjectInfo, ICdgcObjectsInfo } from '@infa-service/cdgc-connection';
import { flow } from 'lodash-es';
import React, { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { ImperativePanelGroupHandle } from 'react-resizable-panels';
import { useParams } from 'react-router-dom';

import { Explorer } from '../../../component/core/explorer/Explorer';
import { Ribbon } from '../../../component/core/main-toolbar/Ribbon';
import PanelError from '../../../component/core/panel-error/PanelError';
import ProgressScreen from '../../../component/core/progress/ProgressScreen';
import { FluentPanel, FluentPanelGroupHorizontal } from '../../../component/core/reasiable-panel/FluentResizablePanel';
import ResizeHandler from '../../../component/core/resize-handler/ResizeHandler';
import { TreeItemType } from '../../../component/core/tree/Tree';
import { useCache } from '../../../context/business/IDQACache/IDQACacheContext';
import { useIDQAPageContext } from '../../../context/business/IDQAPage/IDQAPageContext';
import useDQTranslation from '../../../hooks/useDQTranslation';
import i18nKeys from '../../../i18n/i18nKeys';
import commonStyle from '../../../styles/CommonStyles';
import {
	BrowseServiceApiClone,
	extractAndFormatFilterValue,
	extractFilterType,
	generateHierarchicalValue,
	getFilterValueFromExternalId,
	STATUS,
} from './explorer-utils';
import useStyle from './ExplorerStyle';

export type ExplorerStatus = { status: STATUS.LOADING } | { status: STATUS.SUCCESS } | { status: STATUS.FAILURE } | { status: STATUS.SYNC_EXPLORER };
enum TabId {
	HOME = 'HOME',
}
const PAGE_SIZE = 20;
const ExplorerWrapper: FC = ({ children }) => {
	const {
		controller,
		state: { catalogSrc, artifactDetails },
	} = useIDQAPageContext();
	const { t } = useDQTranslation();
	const { assetId } = useParams<{ assetId: string }>();
	const { extensionClient } = useFabricContext();
	const cache = useCache();
	const { flexStyles, spacingStyles } = commonStyle;
	const commonFlexStyles = flexStyles();
	const commonSpacingStyles = spacingStyles();
	const style = useStyle();
	const [status, setStatus] = React.useState<ExplorerStatus>({ status: STATUS.LOADING });
	const [tableData, setTableData] = useState<TreeItemType>();
	const [selectedTab, setSelectedTab] = useState<TabId>(TabId.HOME);
	const panelGroupRef = React.useRef<ImperativePanelGroupHandle>(null);

	const browseApi = useMemo(() => new BrowseServiceApiClone(), [catalogSrc?.connectionDetails]);
	const tabs: Array<{ id: TabId; label: string; content: () => ReactNode }> = useMemo(
		() => [
			{
				id: TabId.HOME,
				label: 'Home',
				content: () => (
					<Stack.Item>
						<Ribbon />
					</Stack.Item>
				),
			},
		],
		[],
	);
	const writeThroughCache = useCallback((key: string, item: ICdgcObjectInfo) => {
		cache.set<{ catalogExternalId: string | undefined; filterValue: typeof item.filterValues }>(key, {
			catalogExternalId: item.catalogExternalId,
			filterValue: item.filterValues,
		});
	}, []);

	const getChildrens = useCallback(
		async (locations: Array<{ type: string; name: string }>, offset: number, size: number) => {
			if (!catalogSrc) {
				throw new Error('somethig went wrong, please retry!');
			}
			const { connectionDetails, id: catalogueSrcId } = catalogSrc;
			const requestParam: IBrowseConnection = {
				location: [...locations],
				catalogSourceId: catalogueSrcId,
				additionalProperties: {
					mode: 'FabricUI',
				},
				offset,
				size,
			};
			return await browseApi.getChildren({ req: requestParam, connectionId: connectionDetails.srcConnection as string });
		},
		[browseApi, catalogSrc?.connectionDetails],
	);

	const loadMoreNode = useCallback(async (node: TreeItemType) => {
		const { offset, element } = node;
		if (offset) {
			const { successful, cause, content } = await getChildrens(element ? [element] : [], offset, PAGE_SIZE);
			if (!successful || !content) {
				throw new Error(cause);
			}
			return formBranchNode(content);
		}
	}, []);
	const loadChildrens = useCallback(async (node: TreeItemType) => {
		const { element, offset, value } = node;
		if (element) {
			const { successful, cause, content } = await getChildrens([element], offset || 0, PAGE_SIZE);
			if (!successful || !content) {
				throw new Error(cause);
			}
			return formBranchNode(content, value);
		}
	}, []);
	const handleElementSelection = React.useCallback(
		async (value: TreeItemType, parentDetails: TreeItemType) => {
			if (catalogSrc?.id && artifactDetails?.displayName) {
				const { element, terminal, pseudoExternalId, filterValues, label, value: terminalValue } = value;
				const { element: parentElement, pseudoExternalId: parentId, childrens } = parentDetails;
				const filter =
					filterValues && filterValues.length > 0
						? extractAndFormatFilterValue(filterValues)
						: getFilterValueFromExternalId(catalogSrc?.id, artifactDetails.displayName, terminalValue);
				const filterType = filterValues && filterValues.length > 0 ? extractFilterType(filterValues) : null;
				const hierarchy = filter ? filter.split('.') : null;
				if (element && pseudoExternalId && catalogSrc?.id && filter && terminal && hierarchy && parentElement && parentId && filterType) {
					const cacheHit = cache.get<{ catalogExternalId: string }>(pseudoExternalId);
					controller.selectTable({
						id: catalogSrc?.id,
						hierarchy,
						name: label,
						catalogExternalId: cacheHit?.catalogExternalId ? cacheHit.catalogExternalId : pseudoExternalId,
						filterValue: filter,
						filterType,
						schemaDetail: {
							element: parentElement,
							id: parentId,
							size: childrens.length,
						},
					});
				}
			}
		},
		[catalogSrc?.id, artifactDetails?.displayName, tableData],
	);
	const formRootNode = ({ childrens, moreElements }: { childrens: TreeItemType[]; moreElements: boolean }): TreeItemType => ({
		itemType: 'root',
		value: 'root-node',
		label: 'root-node',
		terminal: false,
		offset: PAGE_SIZE,
		moreElements,
		childrens: [...childrens],
	});
	const generateCatalogExternalId = (item: ICdgcObjectInfo, treeItemValue: string): string => {
		if (item.catalogExternalId) {
			return item.catalogExternalId;
		}

		return `${treeItemValue}~${item.element.type}`;
	};

	const generateTreeItemValue = (catalogSrcId: string, nodeLabel: string, parentValue?: string): string =>
		generateHierarchicalValue({ csId: catalogSrcId, nodeLabel, parentValue });

	const generateTreeItem = (item: ICdgcObjectInfo, treeItemValue: string, catalogExternalId: string, moreElements: boolean): TreeItemType => {
		const treeItem: TreeItemType = {
			value: treeItemValue,
			label: item.element.name,
			itemType: item.terminal ? 'leaf' : 'branch',
			element: { type: item.element.type, name: item.element.name },
			filterValues: item.filterValues ? [...item.filterValues] : [],
			terminal: item.terminal,
			moreElements,
			offset: 0,
			isChildrenLoaded: false,
			pseudoExternalId: catalogExternalId,
			childrens: [],
		};
		treeItem.iconBefore = item.terminal ? <TableRegular /> : <FolderRegular />;
		return treeItem;
	};

	const formBranchNode = (response: ICdgcObjectsInfo, parentValue?: string): { childrens: TreeItemType[]; moreElements: boolean } => {
		if (!catalogSrc) {
			throw new Error('catalog src id not found ! retry');
		}

		const { objectsInfo, moreElements } = response;

		const treeItems = objectsInfo.map((item) => {
			const treeItemValue = generateTreeItemValue(catalogSrc?.id, item.element.name, parentValue);
			const catalogExternalId = generateCatalogExternalId(item, treeItemValue);
			const treeItem = generateTreeItem(item, treeItemValue, catalogExternalId, moreElements);
			writeThroughCache(catalogExternalId, item);
			return treeItem;
		});

		return { childrens: treeItems, moreElements };
	};

	const fetchTables = useCallback(async () => {
		try {
			const { successful, cause, content } = await getChildrens([], 0, PAGE_SIZE);
			if (!successful || !content) {
				throw new Error(cause);
			}
			const formatInitialResponse = flow(formBranchNode, formRootNode);
			setTableData(formatInitialResponse(content));
			setStatus({ status: STATUS.SUCCESS });
		} catch (err) {
			setStatus({ status: STATUS.FAILURE });
		}
	}, [browseApi]);

	const resyncExplorer = useCallback(async () => {
		setStatus({ status: STATUS.LOADING });
		controller.removeTableDetails();
		let srcArtifactName = artifactDetails?.displayName;
		if (!srcArtifactName) {
			const artifactDetails = await extensionClient.itemCrud.getItem({ objectId: assetId });
			srcArtifactName = artifactDetails.displayName;
		}
		if (srcArtifactName && catalogSrc?.name) {
			try {
				await fetchTables();
			} catch (err) {
				setStatus({ status: STATUS.SUCCESS });
				extensionClient.notification.open({
					title: t(i18nKeys.main.syncExplorerFailedTitle),
					message: t(i18nKeys.main.syncExplorerFailedDesc),
					notificationType: NotificationType.Error,
				});
			}
		}
		return () => {
			setTableData(undefined);
		};
	}, [artifactDetails?.displayName]);

	useEffect(() => {
		try {
			if (!tableData) {
				fetchTables();
			}
		} catch (err) {
			setStatus({ status: STATUS.FAILURE });
		}
	}, [catalogSrc]);

	return (
		<>
			{status.status === STATUS.SUCCESS || status.status === STATUS.SYNC_EXPLORER ? (
				<Stack className={mergeClasses(style.backgroundColor, commonSpacingStyles.height100)}>
					<Stack.Item>
						<TabList
							selectedValue={selectedTab}
							onTabSelect={(_, data: SelectTabData) => {
								setSelectedTab(data.value as TabId);
							}}
						>
							{tabs.map(({ id, label }) => (
								<Tab key={id} id={id} value={id}>
									{label}
								</Tab>
							))}
						</TabList>
					</Stack.Item>
					<Stack.Item>
						<Ribbon />
					</Stack.Item>
					<Stack.Item className={mergeClasses(style.flexGrow, style.maxHeight80)}>
						<FluentPanelGroupHorizontal ref={panelGroupRef} className={style.borderRadius}>
							<Explorer
								status={status}
								loadChildrens={loadChildrens}
								loadMoreNodes={loadMoreNode}
								drawerHeaderTitle={t(i18nKeys.main.explorer)}
								treeTitle={artifactDetails?.displayName}
								enableExpandablePanel={true}
								onTreeSelectionChange={handleElementSelection}
								panelGroupRef={panelGroupRef}
								tableData={tableData}
								onExplorerRefresh={resyncExplorer}
							/>

							<ResizeHandler />
							<FluentPanel>{children}</FluentPanel>
						</FluentPanelGroupHorizontal>
					</Stack.Item>
				</Stack>
			) : (
				<Stack className={commonSpacingStyles.height100}>
					<Stack.Item className={commonFlexStyles.flexGow1}>
						{status.status === STATUS.LOADING ? (
							<ProgressScreen message={t(i18nKeys.main.loadingExplorer)} icon={<DataUsageRegular className={style.dataIconSize} />} />
						) : (
							<PanelError
								title={t(i18nKeys.main.tableExtractionFailed)}
								errorDetails={t(i18nKeys.main.sourceExtractionFailed)}
								errorBtnClb={resyncExplorer}
								errorBtns={[{ id: 'retryExtraction', label: t(i18nKeys.main.retry), btnProps: { appearance: 'primary', size: 'small' } }]}
							/>
						)}
					</Stack.Item>
				</Stack>
			)}
		</>
	);
};

export default ExplorerWrapper;
